<template>
    <div class="review-container">
        <div class="user-photo web">
            <img :src="displayedUserPhoto" />
        </div>
        <div class="review-info">
            <div class="review-header row-wrap">
                <div class="review-title web">
                    <div class="review-overview">
                        <h4>{{ titleCaseUserName }}</h4>
                    </div>
                    <div class="review-stars">
                        <span> {{ overallRating }} </span>
                        <StarRating :rating="overallRating" :gold="false" />
                        <div v-if="gpa != null">Got {{ formattedGPA }} GPA with {{ professor }} in {{formattedQuarter}} </div>
						<div v-else>Taken with {{ professor }} in {{formattedQuarter}} </div>
                    </div>
                </div>
                <div class="review-title mobile">
                    <div class="left-group row">
                        <div class="user-photo mobile">
                            <img :src="displayedUserPhoto" />
                            <h4>{{ titleCaseUserName }}</h4>
                        </div>
                        <h6>{{ formattedQuarter }}</h6>
                    </div>
                    <div class="right-group row">
                        <div class="review-stars">
                            <span> {{ overallRating }} </span>
                            <StarRating :rating="overallRating" :gold="false" />
                        </div>
                        <h6>with {{ professor }}</h6>
                    </div>
                </div>
            </div>
            <div class="review-body">
                <div v-if="review && showReviews">
                    <h6 class="faded">Overall Review</h6>
                    <p>{{ review }}</p>
                </div>

                <div v-if="tips && showTips">
                    <h6 class="faded">Tips for Future Students</h6>
                    <p>{{ tips }}</p>
                </div>
                <div class="review-ratings web" id="review-ratings">
                    <div>
                        <div>
                            <img src="@/assets/icons/metrics/workload-icon.svg" />
                        </div>
                        <div class="rating-label">
                            <div class="label"> Workload </div>
                            <div class="rating"> {{ workloadRating }}/5 </div>
                        </div>
                    </div>
                    <div>
                        <div>
                            <img src="@/assets/icons/metrics/difficulty-icon.svg" />
                        </div>
                        <div class="rating-label">
                            <div class="label"> Difficulty </div>
                            <div class="rating"> {{ difficultyRating }}/5 </div>
                        </div>
                    </div>
                    <div>
                        <div class="rating">
                            <img src="@/assets/icons/metrics/professor-icon.svg" />
                        </div>
                        <div class="rating-label">
                            <div class="label"> Professor </div>
                            <div class="rating"> {{ professorRating }}/5 </div>
                        </div>
                    </div>
                    <div>
                        <div>
                            <img src="@/assets/icons/metrics/relevance-icon.svg" />
                        </div>
                        <div class="rating-label">
                            <div class="label"> Relevance </div>
                            <div class="rating"> {{ relevanceRating }}/5</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="review-footer">
                <h6 v-if="date">Written on {{ date }}</h6>
            </div>
        </div>
    </div>
</template>

<script>
import StarRating from "@/components/other/StarRating";

export default {
    name: "QualitativeReview",
    // This is the "parameter" to this object, we treat it as data
    props: {
        appreciations: Number,
        userName: String,
        userPhoto: String,
        date: String,
		gpa: Number,
		hours: Number,
        timeTaken: String,
        yearTaken: Number,
        professor: String,
        overallRating: Number,
        workloadRating: Number,
        difficultyRating: Number,
        professorRating: Number,
        relevanceRating: Number,
		version: Number,
        review: String,
        tips: String,
        footer: Boolean,
        showTips: Boolean,
        showReviews: Boolean
    },

    components: {
        StarRating
    },

    computed: {
        formattedQuarter() {
            return this.timeTaken.charAt(0).toUpperCase() + this.timeTaken.substring(1).toLowerCase();
        },

        titleCaseUserName() {
			if (!this.userName) {
				return "Anonymous";
			}
            return this.userName.toLowerCase().replace(/(^|\s)\S/g, function(t) {
                return t.toUpperCase();
            });
        },

        displayedUserPhoto() {
            let url = this.userPhoto && this.userPhoto !== "" ? this.userPhoto : require("@/assets/icons/other/avatar-icon.svg")
            return url
        },

        // TODO: Change this with actual GPA
        formattedGPA() {
            let grades = [
				"< 2.1",
				"2.2 - 2.7",
				"2.8 - 3.1",
				"3.2 - 3.6",
				"3.7 - 4.0"
			];
            return grades[this.gpa - 1];
        }
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "@/styles/_main.scss";

.faded {
    color: #555;
}

.review-body {
    & > div {
        margin: 24px 0;
    }

    p {
        margin: 0;
    }

    p,
    h6 {
        opacity: 0.6;
    }
}

.review-container {
    display: flex;
    padding-top: 24px;
    flex-wrap: nowrap;
}

.review-footer {
    display: flex;
    opacity: 0.6;
    padding-bottom: 24px;

    div {
        margin: 24px 0;
    }

    div > span,
    div > p {
        line-height: 200%;
    }

    h6 {
        opacity: 0.4;
        margin-bottom: 12px;
    }

    img {
        margin-right: 3px;
    }

    p,
    span {
        margin: 0;
        opacity: 0.6;
    }
}

.review-info {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    width: 100%;
}

.review-overview {
    display: flex;

    h4 {
        margin-right: 12px;
    }
    h6 {
        opacity: 0.4;
        line-height: 200%;
    }
}

.review-title {
    flex: 1;

    &.mobile {
        .left-group h6 {
            opacity: 0.6;
        }

        .right-group h6 {
            align-self: flex-start;
            color: #999999;
        }

        & > div {
            justify-content: space-between;
            align-items: center;
            margin-top: 5px;

            > :nth-child(1) {
                display: flex;
                flex-flow: row;
                align-items: center;
            }
        }

        span {
            margin: -5px 5px 0 0;
        }
    }
}

.review-ratings {
    display: flex;
    justify-content: space-evenly;
    flex-wrap: wrap;
    border: 1px solid #F0C9C3;
    border-radius: 12px;
    padding: 20px 24px;


    & > div {
        display: flex;
        align-items: center;
    }

    img {
        height: 24px;
    }

    .rating-label {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        text-align: center;

        div.rating {
            color: #E3C855;
            font-size: 16px;
        }

        div.label {
            font-size: 14px;
            margin: 0 5px;
            color: #999999;
        }
    }
}

.review-stars {
    display: flex;

    div {
        color: #464646;
        font-size: 14px;
        line-height: 200%;
        margin: 0px 8px;
    }

    span {
        color: #feb14a;
        font-size: 20px;
        margin-right: 6px;
    }
}

.user-photo {
    img {
        border-radius: 100%;
        border: 1px solid #e6e6e6;
    }

    &.web img {
        height: 48px;
        margin: 0 24px;
    }

    &.mobile img {
        height: 24px;
        margin: 0;
        margin-right: 12px;
    }

    .review-info {
        margin-left: 24px;
    }
}

[class^="tooltip--"]:before {
    width: fit-content;
}

@media only screen and (max-width: 1200px) {
    .review-ratings {
        & > div {
            justify-content: center;
            width: 50%;
        }
    }
}

@media only screen and (max-width: $breakpoint-small) {
    .review-ratings {
        flex-wrap: wrap;

        & > div {
            width: auto;
        }
    }

    #reviews-section .section-header {
        padding-bottom: 0;
    }
}
</style>
